import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cargoApi, type DeliveryListItem, type DeliveryDetail } from '@/api/cargo';
import { PageHead, DataTable, Drawer, SectionCard, Field, StatusBadge, type Col } from '@/shared/ui/kit';
import { useToast } from '@/app/providers/ToastProvider';
import { DELIVERY_STATUS, SHIPMENT_STATUS_COLOR } from '@/shared/cargo';

export function DeliveriesPage() {
  const toast = useToast();
  const [editId, setEditId] = useState<string | null | undefined>(undefined);
  const q = useQuery({ queryKey: ['cargo', 'deliveries'], queryFn: cargoApi.deliveries });

  const cols: Col<DeliveryListItem>[] = [
    { key: 'DeliveryNo', label: 'Дугаар', width: 160, render: (r) => <button className="ct-link" onClick={(e) => { e.stopPropagation(); setEditId(r.Id); }}>{r.DeliveryNo}</button> },
    { key: 'DriverName', label: 'Жолооч', render: (r) => r.DriverName ?? '—' },
    { key: 'Status', label: 'Төлөв', render: (r) => <StatusBadge label={r.StatusLabel} color={r.Status === 3 ? 'var(--ts-ok)' : 'var(--ts-info)'} /> },
    { key: 'Address', label: 'Хаяг', render: (r) => r.Address ?? '—' },
    { key: 'ShipmentCount', label: 'Илгээмж', align: 'right', render: (r) => r.ShipmentCount },
  ];

  return (
    <div>
      <PageHead title="Хүргэлт" subtitle="Дотоодын last-mile хүргэлт, жолооч хуваарилалт."
        actions={<button className="ts-btn ts-btn-primary" onClick={() => setEditId(null)}><i className="bi bi-plus-lg" /> Шинэ хүргэлт</button>} />
      <DataTable cols={cols} rows={q.data ?? []} onRowClick={(r) => setEditId(r.Id)} empty="Хүргэлт алга." />
      {editId !== undefined && <DeliveryDrawer id={editId} onClose={() => setEditId(undefined)} onChanged={() => q.refetch()} toast={toast} />}
    </div>
  );
}

function DeliveryDrawer({ id, onClose, onChanged, toast }: { id: string | null; onClose: () => void; onChanged: () => void; toast: (m: string, t?: 'info' | 'success' | 'warning' | 'danger') => void }) {
  const isNew = id === null;
  const [detail, setDetail] = useState<DeliveryDetail | null>(null);
  const [form, setForm] = useState({ FKDriverId: '', Address: '', Note: '', ShipmentIds: [] as string[] });
  const [busy, setBusy] = useState(false);
  const driversQ = useQuery({ queryKey: ['cargo', 'drivers'], queryFn: cargoApi.drivers });
  const shipmentsQ = useQuery({ queryKey: ['cargo', 'shipments', ''], queryFn: () => cargoApi.shipments() });

  useEffect(() => {
    if (!isNew && id) cargoApi.delivery(id).then((d) => { setDetail(d); setForm({ FKDriverId: d.FKDriverId ?? '', Address: d.Address ?? '', Note: d.Note ?? '', ShipmentIds: d.Shipments.map((s) => s.Id) }); });
  }, [id, isNew]);

  // Хүргэлтэд тохирох илгээмж: дотоодод ирсэн / бэлэн / хүргэлтэд гарсан.
  const candidates = (shipmentsQ.data ?? []).filter((s) => s.Status >= 3 && s.Status <= 5);

  async function save() {
    setBusy(true);
    try {
      const d = await cargoApi.saveDelivery(id, { ...form, FKDriverId: form.FKDriverId || null });
      setDetail(d);
      toast('Хадгаллаа.', 'success');
      onChanged();
      if (isNew) onClose();
    } catch (e) { toast((e as Error).message, 'danger'); } finally { setBusy(false); }
  }
  async function markDelivered() {
    if (!detail) return;
    setBusy(true);
    try { setDetail(await cargoApi.markDelivered(detail.Id)); toast('Хүргэгдсэн.', 'success'); onChanged(); } catch (e) { toast((e as Error).message, 'danger'); } finally { setBusy(false); }
  }

  return (
    <Drawer title={isNew ? 'Шинэ хүргэлт' : detail?.DeliveryNo ?? 'Хүргэлт'} open onClose={onClose}
      footer={<><button className="ts-btn ts-btn-ghost" onClick={onClose}>Хаах</button>{detail && detail.Status !== 3 && <button className="ts-btn" disabled={busy} onClick={markDelivered}><i className="bi bi-check2-circle" /> Хүргэгдсэн</button>}<button className="ts-btn ts-btn-primary" disabled={busy} onClick={save}><i className="bi bi-check2" /> Хадгалах</button></>}>
      {detail && <div style={{ marginBottom: 6 }}><StatusBadge label={detail.StatusLabel} color={detail.Status === 3 ? 'var(--ts-ok)' : 'var(--ts-info)'} /> <span className="ts-page-sub">{DELIVERY_STATUS[detail.Status]}</span></div>}
      <SectionCard icon="bi-truck" title="Хүргэлтийн мэдээлэл">
        <div className="ts-row">
          <div style={{ gridColumn: 'span 6' }}><Field label="Жолооч"><select className="ts-select" value={form.FKDriverId} onChange={(e) => setForm({ ...form, FKDriverId: e.target.value })}><option value="">— сонгох —</option>{driversQ.data?.map((d) => <option key={d.Id} value={d.Id}>{d.Name}</option>)}</select></Field></div>
          <div style={{ gridColumn: 'span 6' }}><Field label="Хаяг"><input className="ts-input" value={form.Address} onChange={(e) => setForm({ ...form, Address: e.target.value })} /></Field></div>
          <div style={{ gridColumn: 'span 12' }}><Field label="Тэмдэглэл"><input className="ts-input" value={form.Note} onChange={(e) => setForm({ ...form, Note: e.target.value })} /></Field></div>
        </div>
      </SectionCard>

      <SectionCard icon="bi-box-seam" title={`Илгээмж (${form.ShipmentIds.length})`} hint="Дотоодод ирсэн / бэлэн илгээмжүүд">
        {candidates.map((s) => (
          <label key={s.Id} className="ts-flex-between" style={{ padding: '6px 0', cursor: 'pointer', borderBottom: '1px solid var(--ts-border)' }}>
            <span className="ts-flex-row">
              <input type="checkbox" checked={form.ShipmentIds.includes(s.Id)} onChange={(e) => setForm((f) => ({ ...f, ShipmentIds: e.target.checked ? [...f.ShipmentIds, s.Id] : f.ShipmentIds.filter((x) => x !== s.Id) }))} />
              {s.TrackingNo} · {s.CustomerName}
            </span>
            <StatusBadge label={s.StatusLabel} color={SHIPMENT_STATUS_COLOR[s.Status]} />
          </label>
        ))}
        {!candidates.length && <div className="ts-page-sub">Хүргэх боломжтой илгээмж алга.</div>}
      </SectionCard>
    </Drawer>
  );
}
